import { jwtDecode } from 'jwt-decode';

// OAuth2 configuration for WSO2 Identity Server
const AUTH_CONFIG = {
  // Produzione WSO2 Identity Server (porta 443) con redirect production URL
  authorizationUrl: 'https://is.oxy25.profesia.it:443/oauth2/authorize',
  tokenUrl: 'https://is.oxy25.profesia.it:443/oauth2/token',
  clientId: 'obtkrq6EwKqnm6nWIm4LcSoJ8Ysa', // WSO2 IS OAuth2 Client ID
  redirectUri: 'https://oxy25.profesia.it/callback', // Production redirect URL
  responseType: 'code',
  scope: 'openid profile email'
};

// PKCE utility functions
const generateCodeVerifier = () => {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return base64URLEncode(array);
};

const generateCodeChallenge = async (codeVerifier) => {
  const encoder = new TextEncoder();
  const data = encoder.encode(codeVerifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return base64URLEncode(new Uint8Array(digest));
};

const base64URLEncode = (buffer) => {
  return btoa(String.fromCharCode(...buffer))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
};

// Generate OAuth2 authorization URL with PKCE
export const getAuthorizationUrl = async () => {
  // Clear any existing auth data to ensure clean state
  localStorage.removeItem('oauth_state');
  localStorage.removeItem('pkce_code_verifier');
  localStorage.removeItem('pkce_state');
  sessionStorage.removeItem('oauth_state');
  sessionStorage.removeItem('pkce_code_verifier');
  sessionStorage.removeItem('pkce_state');
  
  // Generate separate states for OAuth2 and PKCE
  const oauthState = generateState();
  const pkceState = generateState();
  const codeVerifier = generateCodeVerifier();
  const codeChallenge = await generateCodeChallenge(codeVerifier);

  console.log('[DEBUG] Stati generati:', { oauthState, pkceState });

  // Try multiple storage approaches
  let storageSuccess = false;
  let storageType = '';

  // Approach 1: Try localStorage first
  try {
    localStorage.setItem('oauth_state', oauthState);
    localStorage.setItem('pkce_state', pkceState);
    localStorage.setItem('pkce_code_verifier', codeVerifier);
    // Immediate verification
    if (localStorage.getItem('oauth_state') === oauthState) {
      storageSuccess = true;
      storageType = 'localStorage';
      console.log('[DEBUG] Stato salvato in localStorage:', oauthState);
    }
  } catch (error) {
    console.log('[DEBUG] localStorage fallito:', error);
  }

  // Approach 2: Try sessionStorage if localStorage failed
  if (!storageSuccess) {
    try {
      sessionStorage.setItem('oauth_state', oauthState);
      sessionStorage.setItem('pkce_state', pkceState);
      sessionStorage.setItem('pkce_code_verifier', codeVerifier);
      // Immediate verification
      if (sessionStorage.getItem('oauth_state') === oauthState) {
        storageSuccess = true;
        storageType = 'sessionStorage';
        console.log('[DEBUG] Stato salvato in sessionStorage:', oauthState);
      }
    } catch (error) {
      console.log('[DEBUG] sessionStorage fallito:', error);
    }
  }

  // Approach 3: Use window object as last resort
  if (!storageSuccess) {
    console.log('[DEBUG] Entrambi i metodi di storage falliti, uso window object');
    window.__oauth_state = oauthState;
    window.__pkce_state = pkceState;
    window.__pkce_code_verifier = codeVerifier;
    storageSuccess = true;
    storageType = 'window';
  }

  console.log(`[DEBUG] Authentication state stored using: ${storageType}`);
  console.log('[DEBUG] Stato inviato nell\'URL di autorizzazione:', oauthState);

  const params = new URLSearchParams({
    response_type: AUTH_CONFIG.responseType,
    client_id: AUTH_CONFIG.clientId,
    redirect_uri: AUTH_CONFIG.redirectUri,
    scope: AUTH_CONFIG.scope,
    state: oauthState, // OAuth2 state for CSRF protection
    code_challenge: codeChallenge,
    code_challenge_method: 'S256',
    prompt: 'login'
  });

  return `${AUTH_CONFIG.authorizationUrl}?${params.toString()}`;
};

// Generate random state for CSRF protection
const generateState = () => {
  const state = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
  console.log('[DEBUG] Stato generato:', state);
  return state;
};

// Store state in localStorage
export const storeState = (state) => {
  localStorage.setItem('oauth_state', state);
};

// Verify state
export const verifyState = (state) => {
  console.log('[DEBUG] Parametro state ricevuto dal callback:', state);
  let storedState = null;
  let storageType = '';
  // Check localStorage first
  storedState = localStorage.getItem('oauth_state');
  if (storedState) {
    storageType = 'localStorage';
  }
  // Fallback to sessionStorage
  if (!storedState) {
    storedState = sessionStorage.getItem('oauth_state');
    if (storedState) {
      storageType = 'sessionStorage';
    }
  }
  // Fallback to window object
  if (!storedState && window.__oauth_state) {
    storedState = window.__oauth_state;
    storageType = 'window';
  }
  // Debug logging
  console.log('[DEBUG] Verifica stato:', {
    receivedState: state,
    storedState: storedState,
    storageType: storageType,
    windowState: window.__oauth_state,
    allLocalStorage: Object.keys(localStorage).reduce((acc, key) => {
      acc[key] = localStorage.getItem(key);
      return acc;
    }, {}),
    allSessionStorage: Object.keys(sessionStorage).reduce((acc, key) => {
      acc[key] = sessionStorage.getItem(key);
      return acc;
    }, {})
  });
  // If no stored state found anywhere
  if (!storedState) {
    console.error('[DEBUG] Nessuno stato trovato in nessuna memoria. Possibile refresh browser o scadenza sessione.');
    return false;
  }
  // If no state parameter provided
  if (!state) {
    console.error('[DEBUG] Nessun parametro state fornito nel callback.');
    return false;
  }
  const isValid = state === storedState;
  // Only remove stored state if verification is successful
  if (isValid) {
    if (storageType === 'localStorage') {
      localStorage.removeItem('oauth_state');
    } else if (storageType === 'sessionStorage') {
      sessionStorage.removeItem('oauth_state');
    } else if (storageType === 'window') {
      delete window.__oauth_state;
    }
    console.log(`[DEBUG] Verifica stato OK, rimosso oauth_state da ${storageType}`);
  } else {
    console.error('[DEBUG] Verifica stato FALLITA - i valori non corrispondono');
    console.error('[DEBUG] Stato ricevuto:', state);
    console.error('[DEBUG] Stato memorizzato:', storedState);
  }
  return isValid;
};

// Exchange authorization code for tokens with PKCE
export const exchangeCodeForTokens = async (code) => {
  let codeVerifier = localStorage.getItem('pkce_code_verifier');
  
  // Fallback to sessionStorage and window object
  if (!codeVerifier) {
    codeVerifier = sessionStorage.getItem('pkce_code_verifier');
  }
  if (!codeVerifier && window.__pkce_code_verifier) {
    codeVerifier = window.__pkce_code_verifier;
  }
  
  if (!codeVerifier) {
    console.error('PKCE code verifier not found in any storage location');
    throw new Error('PKCE code verifier not found. PKCE flow may have been compromised.');
  }
  
  console.log('Using PKCE code verifier for token exchange');

  const tokenData = {
    grant_type: 'authorization_code',
    code: code,
    redirect_uri: AUTH_CONFIG.redirectUri,
    client_id: AUTH_CONFIG.clientId,
    code_verifier: codeVerifier
    // Rimosso client_secret per usare solo PKCE (public client)
  };

  try {
    console.log('Making token exchange request to:', AUTH_CONFIG.tokenUrl);
    console.log('Token request data:', tokenData);
    
    const response = await fetch(AUTH_CONFIG.tokenUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams(tokenData)
    });

    console.log('Token exchange response status:', response.status);
    console.log('Token exchange response headers:', response.headers);
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('Token exchange failed with full response:', errorText);
      console.error('Response status:', response.status);
      console.error('Response statusText:', response.statusText);
      
      // Try to parse as JSON to get more details
      try {
        const errorJson = JSON.parse(errorText);
        console.error('Parsed error details:', errorJson);
      } catch (e) {
        console.error('Error response is not JSON:', errorText);
      }
      
      throw new Error(`Token exchange failed: ${response.status} ${response.statusText} - ${errorText}`);
    }

    const tokens = await response.json();
    
    // Store tokens
    localStorage.setItem('access_token', tokens.access_token);
    localStorage.setItem('id_token', tokens.id_token);
    localStorage.setItem('refresh_token', tokens.refresh_token);

      // Debug log: token response
      console.log('[DEBUG] Token response:', tokens);
      if (tokens.access_token) {
        console.log('[DEBUG] Access Token:', tokens.access_token);
      } else {
        console.warn('[DEBUG] No access_token in response:', tokens);
      }
    // Clean up PKCE values
    localStorage.removeItem('pkce_code_verifier');
    localStorage.removeItem('pkce_state');

    return tokens;
  } catch (error) {
    console.error('Error exchanging code for tokens:', error);
    // Clean up PKCE values on error
    localStorage.removeItem('pkce_code_verifier');
    localStorage.removeItem('pkce_state');
    throw error;
  }
};

// Get stored access token
export const getAccessToken = () => {
  return localStorage.getItem('access_token');
};

// Get stored ID token
export const getIdToken = () => {
  return localStorage.getItem('id_token');
};

// Decode and get user info from ID token
export const getUserInfo = () => {
  const idToken = getIdToken();
  if (!idToken) return null;

  try {
    const decoded = jwtDecode(idToken);
    return {
      sub: decoded.sub,
      name: decoded.name || decoded.preferred_username,
      email: decoded.email,
      given_name: decoded.given_name,
      family_name: decoded.family_name,
      picture: decoded.picture
    };
  } catch (error) {
    console.error('Error decoding ID token:', error);
    return null;
  }
};

// Check if user is authenticated
export const isAuthenticated = () => {
  const token = getAccessToken();
  if (!token) return false;

  try {
    const decoded = jwtDecode(token);
    return decoded.exp * 1000 > Date.now();
  } catch (error) {
    return false;
  }
};

// Logout function
export const logout = async () => {
  // Semplice logout: pulizia storage e redirect alla home
  localStorage.removeItem('access_token');
  localStorage.removeItem('id_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('oauth_state');
  localStorage.removeItem('pkce_code_verifier');
  localStorage.removeItem('pkce_state');
  // Redirect alla home produzione
  window.location.href = 'https://oxy25.profesia.it/';
};

// Get authorization header for API calls
export const getAuthHeader = () => {
  const token = getAccessToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Clear all authentication state (useful for debugging/recovery)
export const clearAuthState = () => {
  localStorage.removeItem('access_token');
  localStorage.removeItem('id_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('oauth_state');
  localStorage.removeItem('pkce_code_verifier');
  localStorage.removeItem('pkce_state');
  console.log('Authentication state cleared');
};

// Ottieni token applicazione via client credentials grant
export const getAppToken = async () => {
  const clientId = AUTH_CONFIG.clientId;
  const clientSecret = 'YcBAOlfwB9J45RaSWYam_mKOUn4SO7V1WmfPtopfQP8a'; // Client Secret produzione
  const tokenUrl = AUTH_CONFIG.tokenUrl;

  const body = new URLSearchParams({
    grant_type: 'client_credentials',
    client_id: clientId,
    client_secret: clientSecret,
    scope: 'openid'
  });

  const response = await fetch(tokenUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body
  });

  if (!response.ok) throw new Error('Token application error');
  const data = await response.json();
  return data.access_token;
};
